/**
 * Logger Utility - Configurable Debug Logging System
 * 
 * CREATED: Dec 2024
 * UPDATED: Dec 2024 - Added automatic caller detection
 * WHO: GitHub Copilot (Claude Sonnet 4.5)
 * WHY: Centralized logging with configurable levels for production/development
 * 
 * PURPOSE:
 * - Provides consistent logging interface across application
 * - Configurable via environment variables
 * - Prevents logging in production when not needed
 * - Supports multiple log levels (ERROR, WARN, INFO, DEBUG, TRACE)
 * - Automatically detects caller function/method and file location
 * 
 * CONFIGURATION:
 * Set VUE_APP_ENV_LOG_LEVEL in .env files:
 * - .env.production: VUE_APP_ENV_LOG_LEVEL=ERROR (or omit for no logging)
 * - .env.development: VUE_APP_ENV_LOG_LEVEL=DEBUG
 * - .env.local: VUE_APP_ENV_LOG_LEVEL=TRACE
 * 
 * USAGE:
 * import Logger from '@/utils/Logger'
 * 
 * // Basic usage
 * const log = Logger.create('ComponentName')
 * log.debug('Debug message')
 * 
 * // With automatic caller detection
 * const log = Logger.create('ComponentName', { showCaller: true })
 * log.debug('Shows function name')  // Output includes function name
 * 
 * // With file and line numbers
 * const log = Logger.create('ComponentName', { showCaller: true, showLineNumbers: true })
 * log.debug('Shows everything')  // Output includes function, file, and line number
 * 
 * EXAMPLES:
 * // In a component:
 * const log = Logger.create('TaskEntry', { showCaller: true })
 * log.debug('Loading folders:', folders)
 * // Output: [timestamp] [DEBUG] [TaskEntry] preparePage() Loading folders: [...]
 * 
 * // With line numbers:
 * const log = Logger.create('store/setFolders', { showCaller: true, showLineNumbers: true })
 * log.info('Setting folders', { count: folders.length })
 * // Output: [timestamp] [INFO] [store/setFolders] setFolders() store.js:945 Setting folders {...}
 */

const LogLevel = {
	NONE: 0,    // No logging
	ERROR: 1,   // Critical errors only
	WARN: 2,    // Warnings and errors
	INFO: 3,    // Informational messages, warnings, and errors
	DEBUG: 4,   // Debug information (default for development)
	TRACE: 5    // Verbose trace logging (for deep debugging)
}

class Logger {
	constructor(namespace = 'App', options = {}) {
		this.namespace = namespace
		this.showCaller = options.showCaller !== undefined ? options.showCaller : false
		this.showLineNumbers = options.showLineNumbers !== undefined ? options.showLineNumbers : false
		
		// Get log level from environment variable
		// If not set or invalid, default to NONE (no logging)
		const envLogLevel = process.env.VUE_APP_ENV_LOG_LEVEL
		
		if (!envLogLevel || envLogLevel === '') {
			this.level = LogLevel.NONE
		} else {
			const level = envLogLevel.toUpperCase()
			this.level = LogLevel[level] !== undefined ? LogLevel[level] : LogLevel.NONE
		}
		
		// Store whether console methods are available
		this.hasConsole = typeof console !== 'undefined'
	}
	
	/**
	 * Create a new logger instance with a namespace
	 * @param {string} namespace - Name to prefix log messages (component/module name)
	 * @param {Object} options - Configuration options
	 * @param {boolean} options.showCaller - Automatically detect and show caller function/method
	 * @param {boolean} options.showLineNumbers - Show file name and line number
	 * @returns {Logger} Logger instance
	 * 
	 * @example
	 * // Basic usage
	 * const log = Logger.create('TaskEntry')
	 * 
	 * // With automatic caller detection
	 * const log = Logger.create('TaskEntry', { showCaller: true })
	 * 
	 * // With file and line numbers
	 * const log = Logger.create('TaskEntry', { showCaller: true, showLineNumbers: true })
	 */
	static create(namespace, options = {}) {
		return new Logger(namespace, options)
	}
	
	/**
	 * Get the current log level name
	 * @returns {string} Current log level
	 */
	getLogLevel() {
		const levelName = Object.keys(LogLevel).find(key => LogLevel[key] === this.level)
		return levelName || 'NONE'
	}
	
	/**
	 * Extract caller information from stack trace
	 * @private
	 */
	_getCallerInfo() {
		if (!this.showCaller && !this.showLineNumbers) {
			return null
		}
		
		try {
			// Create error to get stack trace
			const stack = new Error().stack
			if (!stack) return null
			
			// Split stack into lines
			const lines = stack.split('\n')
			
			// Find the first line that's not from Logger.js
			// Typically: line 0 = "Error", line 1 = _getCallerInfo, line 2 = _formatMessage, 
			// line 3 = error/warn/info/debug/trace, line 4 = actual caller
			let callerLine = null
			for (let i = 4; i < lines.length; i++) {
				const line = lines[i]
				if (line && !line.includes('Logger.js') && !line.includes('at Logger.')) {
					callerLine = line
					break
				}
			}
			
			if (!callerLine) return null
			
			// Parse the stack line
			// Format: "    at functionName (file:line:column)" or "    at file:line:column"
			const match = callerLine.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/)
			
			if (!match) return null
			
			const functionName = match[1] || 'anonymous'
			const filePath = match[2]
			const lineNumber = match[3]
			
			// Extract just the filename from the full path
			const fileName = filePath.split('/').pop().split('\\').pop()
			
			return {
				functionName: functionName.trim(),
				fileName,
				lineNumber,
				fullPath: filePath
			}
		} catch (error) {
			return null
		}
	}
	
	/**
	 * Format log message with timestamp, namespace, and optional caller info
	 * @private
	 */
	_formatMessage(level, ...args) {
		const timestamp = new Date().toISOString()
		
		// Get caller information
		const callerInfo = this._getCallerInfo()
		
		// Use file location instead of namespace if available and line numbers are enabled
		let locationInfo = `[${this.namespace}]`
		if (callerInfo && this.showLineNumbers) {
			locationInfo = `[${callerInfo.fileName}:${callerInfo.lineNumber}]`
		}
		
		let prefix = `[${timestamp}] ${("[" + level + "]").padEnd(7)} ${locationInfo.padEnd(25)}`
		
		// Add function name if enabled
		if (callerInfo && this.showCaller) {
			prefix += ` ${(`[${callerInfo.functionName}()]`).padEnd(30)}`
		}
		
		return [prefix, ...args]
	}
	
	/**
	 * Log error messages (critical errors that need attention)
	 * @param {...any} args - Message and optional data to log
	 */
	error(...args) {
		if (this.hasConsole && this.level >= LogLevel.ERROR) {
			console.error(...this._formatMessage('ERROR', ...args))
		}
	}
	
	/**
	 * Log warning messages (potential issues that aren't critical)
	 * @param {...any} args - Message and optional data to log
	 */
	warn(...args) {
		if (this.hasConsole && this.level >= LogLevel.WARN) {
			console.warn(...this._formatMessage('WARN', ...args))
		}
	}
	
	/**
	 * Log informational messages (important events/milestones)
	 * @param {...any} args - Message and optional data to log
	 */
	info(...args) {
		if (this.hasConsole && this.level >= LogLevel.INFO) {
			console.log(...this._formatMessage('INFO', ...args))
		}
	}
	
	/**
	 * Log debug messages (detailed information for debugging)
	 * @param {...any} args - Message and optional data to log
	 */
	debug(...args) {
		if (this.hasConsole && this.level >= LogLevel.DEBUG) {
			console.log(...this._formatMessage('DEBUG', ...args))
		}
	}
	
	/**
	 * Log trace messages (very verbose execution details)
	 * @param {...any} args - Message and optional data to log
	 */
	trace(...args) {
		if (this.hasConsole && this.level >= LogLevel.TRACE) {
			console.log(...this._formatMessage('TRACE', ...args))
		}
	}
	
	/**
	 * Log a group of related messages
	 * @param {string} groupName - Name of the group
	 * @param {Function} callback - Function containing log statements
	 */
	group(groupName, callback) {
		if (this.hasConsole && this.level > LogLevel.NONE && console.group) {
			console.group(`[${this.namespace}] ${groupName}`)
			callback()
			console.groupEnd()
		} else if (this.level > LogLevel.NONE) {
			callback()
		}
	}
	
	/**
	 * Log execution time of a function
	 * @param {string} label - Label for the timer
	 * @param {Function} fn - Function to time
	 * @returns {Promise|any} Result of the function
	 */
	async time(label, fn) {
		if (this.level >= LogLevel.DEBUG) {
			const startTime = performance.now()
			const timerLabel = `[${this.namespace}] ${label}`
			
			if (this.hasConsole && console.time) {
				console.time(timerLabel)
			}
			
			try {
				const result = await fn()
				
				if (this.hasConsole) {
					if (console.timeEnd) {
						console.timeEnd(timerLabel)
					} else {
						const endTime = performance.now()
						this.debug(`${label} took ${(endTime - startTime).toFixed(2)}ms`)
					}
				}
				
				return result
			} catch (error) {
				if (this.hasConsole && console.timeEnd) {
					console.timeEnd(timerLabel)
				}
				throw error
			}
		} else {
			// If logging is disabled, just execute the function
			return await fn()
		}
	}
	
	/**
	 * Conditionally log based on a condition
	 * @param {boolean} condition - Condition to check
	 * @param {string} level - Log level to use
	 * @param {...any} args - Message and optional data to log
	 */
	logIf(condition, level, ...args) {
		if (condition && this[level]) {
			this[level](...args)
		}
	}
}

// CommonJS export for Node.js backend compatibility
module.exports = Logger
module.exports.default = Logger
module.exports.LogLevel = LogLevel
module.exports.Logger = Logger
